/**
 * The Tenant Theme Adapter
 * ------------------------
 * Phase 4.3 — future white-label support.
 *
 * This adapter is the seam between tenant resolution and runtime theme
 * activation. `tenantThemeResolver` decides *which theme id* a tenant
 * should get; this file adapts that decision into the small, stable
 * shape a runtime consumer (a provider, a bootstrap script, a server
 * render) can act on directly, without knowing anything about
 * resolution priority, metadata escape hatches, or the contract's
 * optional fields.
 *
 * Future API/database tenant theme loading should plug in *before*
 * this layer -- i.e. produce an `ITenantThemeBinding` (from wherever
 * that binding actually lives) and hand it to
 * `resolveTenantThemeBinding`, rather than this file reaching out to
 * fetch one itself. That keeps this adapter, and everything upstream
 * of it, free of I/O.
 *
 * Framework-independent and side-effect-free by design: no React, no
 * browser APIs, no state, no ThemeEngine access, no network or
 * storage calls. Given the same input, it always returns the same
 * output, and it never throws.
 */
import { resolveTenantTheme } from '../resolvers/tenantThemeResolver';
function isNonEmptyString(value) {
    return typeof value === 'string' && value.trim().length > 0;
}
/**
 * Adapts a tenant theme binding into the runtime-facing
 * `{ tenantId?, resolvedThemeId }` shape.
 *
 * Delegates the actual priority logic (tenant custom theme -> tenant
 * default theme -> system fallback theme -> "default") entirely to
 * `resolveTenantTheme`; this function's only job is to also carry the
 * tenant id through when one is available, and to do all of it
 * defensively.
 *
 * Never throws: `undefined`, `null`, and malformed runtime objects
 * (e.g. a `tenantId` that isn't actually a string, despite the static
 * type) are all tolerated -- `tenantId` is simply omitted from the
 * result when it can't be trusted, and `resolvedThemeId` still comes
 * back as a valid theme id because `resolveTenantTheme` itself never
 * throws.
 */
export function resolveTenantThemeBinding(binding) {
    let resolvedThemeId;
    try {
        resolvedThemeId = resolveTenantTheme(binding ?? null);
    }
    catch {
        // Defense in depth -- resolveTenantTheme is documented to never
        // throw, but this adapter makes the same guarantee independently
        // rather than assuming that holds forever.
        resolvedThemeId = 'default';
    }
    let tenantId;
    try {
        if (binding && typeof binding === 'object' && isNonEmptyString(binding.tenantId)) {
            tenantId = binding.tenantId.trim();
        }
    }
    catch {
        tenantId = undefined;
    }
    return tenantId === undefined ? { resolvedThemeId } : { tenantId, resolvedThemeId };
}
